import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle,
  X,
  Check,
  Eye,
  Loader2,
  Bell,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useAlerts,
  useAcknowledgeAlert,
  useDismissAlert,
  type Alert,
  type AlertSeverity,
  getSeverityColor,
  getAlertTypeLabel,
} from '@/hooks/useAlerts';
import { cn } from '@/lib/utils';

function getSeverityIcon(severity: AlertSeverity) {
  switch (severity) {
    case 'critical':
      return <AlertTriangle className="h-4 w-4 text-destructive" />;
    case 'high':
      return <AlertCircle className="h-4 w-4 text-orange-500" />;
    case 'medium':
      return <AlertCircle className="h-4 w-4 text-yellow-500" />;
    case 'low':
      return <Info className="h-4 w-4 text-blue-500" />;
    default:
      return <Info className="h-4 w-4 text-muted-foreground" />;
  }
}

function AlertItem({ alert }: { alert: Alert }) {
  const acknowledgeAlert = useAcknowledgeAlert();
  const dismissAlert = useDismissAlert();
  const [isHovered, setIsHovered] = useState(false);

  const isAcknowledged = !!alert.acknowledged_at;

  return (
    <div
      className={cn(
        'p-3 border-b last:border-b-0 transition-colors',
        !isAcknowledged && 'bg-muted/30',
        isHovered && 'bg-muted/50'
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5">{getSeverityIcon(alert.severity)}</div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className={cn('text-xs', getSeverityColor(alert.severity))}>
              {alert.severity}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(alert.triggered_at), { addSuffix: true })}
            </span>
          </div>

          <p className={cn('text-sm font-medium', !isAcknowledged && 'text-foreground')}>
            {alert.title}
          </p>

          {alert.description && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{alert.description}</p>
          )}

          <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
            <span className="capitalize">{getAlertTypeLabel(alert.alert_type)}</span>
            {alert.client_name && (
              <>
                <span>•</span>
                <Link
                  to={`/clients/${alert.client_id}`}
                  className="text-primary hover:underline"
                >
                  {alert.client_name}
                </Link>
              </>
            )}
            {alert.project_name && (
              <>
                <span>•</span>
                <Link
                  to={`/projects/${alert.project_id}`}
                  className="text-primary hover:underline"
                >
                  {alert.project_name}
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Actions */}
        <div
          className={cn(
            'flex items-center gap-1 transition-opacity',
            isHovered || !isAcknowledged ? 'opacity-100' : 'opacity-0'
          )}
        >
          <TooltipProvider>
            {!isAcknowledged && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => acknowledgeAlert.mutate(alert.id)}
                    disabled={acknowledgeAlert.isPending}
                    aria-label="Mark as seen"
                  >
                    {acknowledgeAlert.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Eye className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Mark as seen</TooltipContent>
              </Tooltip>
            )}

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  onClick={() => dismissAlert.mutate(alert.id)}
                  disabled={dismissAlert.isPending}
                  aria-label="Dismiss alert"
                >
                  {dismissAlert.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <X className="h-3.5 w-3.5" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Dismiss</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </div>
  );
}

export function AlertsPanel() {
  const { data: alerts, isLoading } = useAlerts();

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const hasAlerts = alerts && alerts.length > 0;

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <h3 className="font-semibold">Alerts</h3>
        {hasAlerts && (
          <Badge variant="secondary" className="text-xs">
            {alerts.length} active
          </Badge>
        )}
      </div>

      {/* Alerts list */}
      {hasAlerts ? (
        <ScrollArea className="max-h-96">
          {alerts.map((alert) => (
            <AlertItem key={alert.id} alert={alert} />
          ))}
        </ScrollArea>
      ) : (
        <div className="p-8 text-center">
          <div className="flex justify-center mb-3">
            <div className="rounded-full bg-muted p-3">
              <CheckCircle className="h-6 w-6 text-success" />
            </div>
          </div>
          <p className="font-medium">All clear!</p>
          <p className="text-sm text-muted-foreground mt-1">
            No active alerts at the moment.
          </p>
        </div>
      )}

      {/* Footer */}
      <div className="px-4 py-3 border-t bg-muted/30">
        <Link to="/settings?tab=security">
          <Button variant="ghost" size="sm" className="w-full justify-center text-xs">
            <Bell className="h-3.5 w-3.5 mr-2" />
            Manage Alert Preferences
          </Button>
        </Link>
      </div>
    </div>
  );
}
